import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Typography, Grid, Container, Card, CardContent, Chip, Stack, Avatar, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ReplayIcon from '@mui/icons-material/Replay';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

import Header from './Header';
import { useCatalog } from './contexts/CatalogContext.jsx';
import { getPrimaryImageUrl } from './utils/productImage';

const Home = () => {
  const navigate = useNavigate();
  const { products = [] } = useCatalog();
  const featuredProducts = useMemo(() => products.slice(0, 3), [products]);

  const featureCards = [
    {
      title: 'Casino Look, Table-Top Setup',
      description: 'Skip the bulky table rentals. Roll out realistic blackjack, poker, roulette and craps artwork on any dining or folding table for a fraction of traditional prices.',
      icon: <VerifiedUserIcon color="primary" sx={{ fontSize: 32 }} />
    },
    {
      title: 'Flat-Rate Nationwide Shipping',
      description: 'Each cloth ships in a compact tube with prepaid return labels so you only pay for the rental—not the weight of heavy equipment.',
      icon: <LocalShippingIcon color="primary" sx={{ fontSize: 32 }} />
    },
    {
      title: 'Easy Returns & No Hidden Fees',
      description: 'Keep the cloth for your reserved days, drop it back in the mail, and we handle cleaning. No surprise upcharges or storage headaches.',
      icon: <ReplayIcon color="primary" sx={{ fontSize: 32 }} />
    }
  ];

  const steps = [
    { title: 'Reserve', description: 'Choose your rental window using the live calendar, then lock in the price before your date sells out.' },
    { title: 'Receive', description: 'Your cloth ships 3-5 days before your event with tips for setup, care instructions and a prepaid return label.' },
    { title: 'Return', description: 'Fold, pack and drop off at any UPS location using the supplied label. We handle cleaning so you can focus on the fun.' }
  ];

  const faqs = [
    {
      question: 'How long can I keep the rental cloth?',
      answer: 'Every booking covers 3 days automatically. Need longer? Extend your end date in the calendar and the pricing instantly adjusts based on weekdays, weekends and holidays.'
    },
    {
      question: 'Do you ship outside major cities?',
      answer: 'Yes. We ship anywhere in the continental United States using tracked carriers. Rural deliveries may add one transit day—reserve early to ensure arrival before your party.'
    },
    {
      question: 'What happens if the cloth is damaged?',
      answer: 'Normal wear is covered. If there is significant damage, reach out to support so we can discuss options. Protective mats and care instructions accompany each kit.'
    },
    {
      question: 'Can I order multiple game styles?',
      answer: 'Absolutely. Add each game to your cart with the correct dates and we will bundle the shipment so everything shows up together.'
    }
  ];

  const testimonials = [
    {
      name: 'Maya L.',
      title: 'Event Planner, Vegas Nights ATL',
      quote: 'The colors are rich, the fabric drapes perfectly, and the prepaid label makes returns effortless. DIY Casino Party is now our go-to partner.'
    },
    {
      name: 'Jordan K.',
      title: 'Birthday Host',
      quote: 'Our blackjack tournament felt like a real casino. Booking the cloth and shipping it back were smoother than ordering decorations online.'
    }
  ];

  const stats = [
    { label: 'Events Styled', value: '4,200+' },
    { label: 'On-Time Deliveries', value: '99.2%' },
    { label: 'Average Rating', value: '4.9 / 5' }
  ];

  const goToCatalog = () => {
    navigate('/products');
  };

  return (
    <Box component="main">
      <Box component="section" sx={{ position: 'relative', overflow: 'hidden' }}>
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(135deg, rgba(0,58,39,0.95), rgba(14,21,19,0.95))',
            zIndex: -1
          }}
        />
        <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 }, color: '#fff' }}>
          <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip label="DIY Casino Decor" color="secondary" sx={{ mb: 2, fontWeight: 600, color: '#0e1513' }} />
              <Typography variant="h1" sx={{ mb: 2 }}>
                Bring a Casino Atmosphere to Any Table in Days
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.85)' }}>
                Rentals ship in protective tubes, include prepaid returns, and set up in minutes. Choose your dates, secure the rate that fits your budget, and host a polished casino night without renting heavy furniture.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button variant="contained" color="secondary" size="large" onClick={() => navigate('/products')} startIcon={<CalendarMonthIcon />}>
                  Order Now
                </Button>
                <Button variant="outlined" color="inherit" size="large" onClick={goToCatalog}>
                  View Pricing
                </Button>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4} sx={{ mt: 4 }}>
                {stats.map(stat => (
                  <Box key={stat.label}>
                    <Typography variant="h3">{stat.value}</Typography>
                    <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.7)' }}>{stat.label}</Typography>
                  </Box>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative', borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 25px 60px rgba(0,0,0,0.45)' }}>
                <iframe
                  title="DIY Casino Party Preview"
                  src="https://www.youtube.com/embed/hpfx21VmnqY"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  style={{ display: 'block', width: '100%', height: '360px' }}
                ></iframe>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: 8, backgroundColor: '#f8f5eb' }}>
        <Container maxWidth="lg">
          <Header text="Featured Rentals" subHeader="Start with our most-requested cloths, all ready to ship for your party." />
          <Grid container spacing={4}>
            {featuredProducts.length === 0 && (
              <Grid item xs={12}>
                <Typography variant="body1">Products are loading—check back in a moment.</Typography>
              </Grid>
            )}
            {featuredProducts.map(product => (
              <Grid key={product.slug} item xs={12} md={4}>
                <Card sx={{ height: '100%', borderRadius: 4, boxShadow: '0 20px 60px rgba(0,0,0,0.1)' }}>
                  <Box
                    component="img"
                    src={getPrimaryImageUrl(product)}
                    alt={product.name}
                    sx={{ width: '100%', height: 220, objectFit: 'cover', borderTopLeftRadius: 16, borderTopRightRadius: 16 }}
                  />
                  <CardContent>
                    <Typography variant="overline" sx={{ letterSpacing: '0.3em', color: 'text.secondary' }}>
                      Rental Cloth
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                      {product.description?.slice(0, 120) || 'Premium felt with bold artwork that transforms any table.'}
                    </Typography>
                    <Chip
                      label={`Starting at $${((product.price_cents || 0) / 100).toFixed(2)}`}
                      color="primary"
                      sx={{ mt: 2 }}
                    />
                    <Button
                      fullWidth
                      variant="contained"
                      color="primary"
                      sx={{ mt: 3, borderRadius: 999 }}
                      onClick={() => navigate(`/products/${product.slug}`)}
                    >
                      View Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Button variant="outlined" color="primary" size="large" onClick={() => navigate('/products')}>
              Browse Full Catalog
            </Button>
          </Box>
        </Container>
      </Box>

      <Box component="section" sx={{ py: 8, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {featureCards.map(card => (
              <Grid key={card.title} item xs={12} md={4}>
                <Card sx={{ height: '100%', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 20px 45px rgba(0,0,0,0.1)' }}>
                  <CardContent>
                    {card.icon}
                    <Typography variant="h5" sx={{ mt: 2 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: 8, backgroundColor: '#fdf6ea' }}>
        <Container maxWidth="lg">
          <Header text="How It Works" subHeader="Reserve online, get designer-quality table décor delivered to your door" />
          <Grid container spacing={4}>
            {steps.map(step => (
              <Grid key={step.title} item xs={12} md={4}>
                <Card sx={{ height: '100%', borderRadius: 4, boxShadow: '0 10px 35px rgba(0,0,0,0.08)' }}>
                  <CardContent>
                    <Typography variant="h4" sx={{ mb: 1 }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2">{step.description}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {testimonials.map(testimonial => (
              <Grid key={testimonial.name} item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="body1" sx={{ fontStyle: 'italic' }}>
                      “{testimonial.quote}”
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                      <Avatar>{testimonial.name.charAt(0)}</Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{testimonial.name}</Typography>
                        <Typography variant="body2">{testimonial.title}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ py: 6, backgroundColor: 'background.paper' }}>
        <Container maxWidth="md">
          <Header text="Frequently Asked Questions" />
          {faqs.map(faq => (
            <Accordion key={faq.question}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2">{faq.answer}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Container>
      </Box>

      <Box component="section" sx={{ py: 6 }}>
        <Container maxWidth="md">
          <Typography variant="h2" gutterBottom>
            The Simple Alternative to Full Casino Rentals
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            DIY Casino Party focuses on the one detail that makes the biggest visual impact—the table surface. Rent blackjack, poker, roulette, craps and specialty cloths that ship in a small box, fit standard tables, and cost a fraction of hauling complete casino tables or hiring dealers. Perfect for fundraisers, office parties, graduations and neighborhood poker nights.
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Pricing updates in real time as you select weekdays, weekends and holidays, so you always know what you are paying before checkout. Because inventory is limited for peak weekends and national holidays, we recommend reserving early. Every cloth is cleaned, pressed and inspected between rentals to keep it photo-ready even at an approachable price point.
          </Typography>
          <Typography variant="body1">
            Ready to plan your casino-style gathering? Use the calendar above to see availability, then continue to checkout for secure payment through Square or Stripe. Need suggestions on how many cloths to order or how to stage your tables? Visit our Support page or reach out to our team—we love helping hosts stretch their budget.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
